import PropTypes from 'prop-types';
import ProviderConnectionConnectWithRedirection from './ProviderConnectionConnectWithRedirection';
import CreateManualProviderConnection from 'Components/Organisms/CreateManualProviderConnection/CreateManualProviderConnection';
import Icon from 'Components/Atoms/Icon/Icon';
import classes from './ProviderConnectionConnect.module.css';
import Button from 'Components/Atoms/Button/Button';
import ProviderConnectionConnectWithCredentials from './ProviderConnectionConnectWithCredentials';
import useProviderConnectionActions from 'Hooks/useProviderConnectionActions';
import ProviderConnectionConnectWithSelector from './ProviderConnectionConnectWithSelector';
import ProviderConnectionConnectWithWidget from './ProviderConnectionConnectWithWidget';
import { useTranslation } from 'react-i18next';
import ProviderConnectionConnectWithWidgetStripe from './ProviderConnectionConnectWithWidgetStripe.jsx';
import useConnectorsIncidents from 'Hooks/useConnectorsIncidents';

const SERVICES_WITH_CREDENTIALS = ['afterbanks_v3'];
const SERVICES_WITH_REDIRECTION = ['afterbanks_psd2', 'nordigen', 'indexa_capital'];
const SERVICES_WITH_SELECTOR = ['teller'];
const SERVICES_WITH_WIDGET = ['wealth_reader'];
const SERVICES_WITH_WIDGET_STRIPE = ['stripe'];
const SERVICE_MANUAL = 'manual';

export default function ProviderConnectionConnect(props) {
    const { providerConnectionId, connector, onConnect, onError, onCancel } = props;
    const { checkConnection } = useProviderConnectionActions();
    const { getConnectorHealth, isConnectorBroken } = useConnectorsIncidents();
    const service = connector?.service;

    const { t } = useTranslation('common');

    const handleConnect = (conexionId, credentials) => {
        checkConnection({
            id: conexionId,
            logo_url: connector.logo_url,
            name: connector.name,
            credentials,
        });
        onConnect();
    };

    if (isConnectorBroken(connector)) {
        const { message } = getConnectorHealth(connector);

        return (
            <div>
                <div className="flex items-center justify-center gap-4">
                    <img src={connector.logo_url} alt={connector.name} style={{ height: '4rem' }} />
                    <h4 className="font-medium m-0">{connector.name}</h4>
                </div>
                <div className={classes.statusInfo}>
                    <Icon name="circle-alert" color="currentColor" />
                    <span>{message || t('provider-connections.bank-down-description')}</span>
                </div>

                {onError && (
                    <Button variant="primary" className="mt-6" onClick={onError}>
                        {t('provider-connections.back')}
                    </Button>
                )}
            </div>
        );
    }

    if (SERVICES_WITH_CREDENTIALS.includes(service)) {
        return (
            <ProviderConnectionConnectWithCredentials
                connectorMeta={connector.service_provider_meta}
                providerConnectionId={providerConnectionId}
                connectorId={connector.id}
                providerLogoUrl={connector.logo_url}
                providerName={connector.name}
                onConnect={handleConnect}
                serviceId={connector.service}
                onCancel={onCancel}
            />
        );
    }

    if (SERVICES_WITH_SELECTOR.includes(service)) {
        return (
            <ProviderConnectionConnectWithSelector
                providerConnectionId={providerConnectionId}
                connectorId={connector.id}
                serviceProviderId={connector.service_provider_id}
                providerName={connector.name}
                onConnect={handleConnect}
                onCancel={onCancel}
            />
        );
    }

    if (SERVICES_WITH_REDIRECTION.includes(service)) {
        return (
            <ProviderConnectionConnectWithRedirection
                providerConnectionId={providerConnectionId}
                connectorId={connector.id}
                providerLogoUrl={connector.logo_url}
                providerName={connector.name}
                onCancel={onCancel}
                serviceId={connector.service}
            />
        );
    }

    if (SERVICES_WITH_WIDGET.includes(service)) {
        return (
            <ProviderConnectionConnectWithWidget
                providerConnectionId={providerConnectionId}
                connectorId={connector.id}
                connectorCountryCode={connector.country_code}
                providerLogoUrl={connector.logo_url}
                providerName={connector.name}
                onConnect={handleConnect}
                serviceProviderId={connector.service_provider_id}
                onCancel={onCancel}
            />
        );
    }

    if (SERVICES_WITH_WIDGET_STRIPE.includes(service)) {
        return (
            <ProviderConnectionConnectWithWidgetStripe
                providerConnectionId={providerConnectionId}
                connectorId={connector.id}
                connectorCountryCode={connector.country_code}
                providerLogoUrl={connector.logo_url}
                providerName={connector.name}
                onConnect={handleConnect}
                serviceProviderId={connector.service_provider_id}
                onCancel={onCancel}
            />
        );
    }

    if (service === SERVICE_MANUAL) {
        return <CreateManualProviderConnection connectorId={connector.id} onConnect={onConnect} onCancel={onCancel} />;
    }

    return null;
}

ProviderConnectionConnect.propTypes = {
    onConnect: PropTypes.func.isRequired,
    connector: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    providerConnectionId: PropTypes.string,
    onError: PropTypes.func,
};
